import React from 'react';
import * as Yup from 'yup';
import { TFunction } from 'next-i18next';
import { emailSchema, nameSchema, passwordSchema } from 'utils/validation';
import withFormSubmission, { MergedProps } from 'components/common/withFormSubmission';

export interface IValues {
  age: boolean;
  email: string;
  first_name: string;
  last_name: string;
  primary_market: number;
  is_other_primary_market: boolean;
  password_one: string;
  password_two: string;
}

export interface IProps {
  user?: any;
  disabledEmail?: boolean;
  withButton?: boolean;
  withPassword?: boolean;
  t?: TFunction;
  onSubmit: (
    values: Omit<IValues, 'age' | 'password_one' | 'password_two'> & { password?: string; confirm?: string },
  ) => void;
  bottom?: (disabled: boolean) => React.ReactNode;
}

export type FormComponentProps = MergedProps<IProps, IValues>;

const withJoinForm = withFormSubmission<IProps, IValues>({
  enableReinitialize: true,
  validationSchema: (props: IProps) => {
    const baseSchema = Yup.object({
      age: Yup.bool().oneOf([true]),
      email: emailSchema().required(),
      first_name: nameSchema().required(),
      last_name: nameSchema().required(),
      primary_market: Yup.number().nullable().required(),
    });

    if (!props.withPassword) return baseSchema;

    // `t` is optional on IProps (only JoinView currently passes it), but is required whenever
    // withPassword is on — fall back to an identity function so a future caller enabling
    // withPassword without wiring `t` gets untranslated messages instead of a runtime crash.
    const t = props.t || ((key: string) => key);

    return baseSchema.shape(
      {
        password_one: Yup.string().when('password_two', {
          is: (value: string) => Boolean(value),
          then: (schema) => schema.required().concat(passwordSchema(t)),
        }),
        password_two: Yup.string()
          .when('password_one', {
            is: (value: string) => Boolean(value),
            then: (schema) => schema.required().concat(passwordSchema(t)),
          })
          .when('password_one', (passwordOne: string, schema: Yup.AnySchema) =>
            schema.test({
              name: 'password_one_password_two',
              message: t('common:passwordErrors.confirm_password_does_not_match_new_password'),
              test: (passwordTwo: string) => !passwordOne || passwordTwo === passwordOne,
            }),
          ),
      },
      [['password_one', 'password_two']],
    );
  },
  mapPropsToValues: ({ user }) => ({
    age: false,
    email: user?.email || '',
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    primary_market: user?.is_other_primary_market ? 0 : user?.primary_market?.id || null,
    is_other_primary_market: user?.is_other_primary_market,
    password_one: '',
    password_two: '',
  }),
  handleSubmit: (values, { props: { onSubmit, withPassword } }) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { age, password_one, password_two, ...rest } = values;
    if (rest.primary_market === 0) {
      rest.primary_market = null;
      rest.is_other_primary_market = true;
    } else {
      rest.is_other_primary_market = false;
    }
    if (withPassword && password_one) {
      onSubmit({ ...rest, password: password_one, confirm: password_two });
    } else {
      onSubmit(rest);
    }
  },
});

export default withJoinForm;
